import { Code, Download, ExternalLink, Eye, Info, Maximize2, Minimize2, Printer, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "@/config/runtime";
import { getHeaders } from "@/lib/api";
import { createPanelBus } from "@/lib/panelBus";
import { extractBindingKeys, injectHydrationRuntime } from "@/pages/apps/microApps/hydrationRuntime";
import { useDashboardHydration } from "@/pages/apps/microApps/useDashboardHydration";
import { useMicroAppUpdateTarget } from "./MicroAppUpdateContext";

// ─── Event-based API for opening HTML preview from anywhere ───────────────

export interface HtmlPreviewRequest {
  title: string;
  htmlContent: string;
  attachmentUuid?: string;
  url?: string;
  version?: number;
  lineageUuid?: string;
  /** Published micro app this HTML belongs to (enables live data + "View app"). */
  appUuid?: string;
}

const bus = createPanelBus<HtmlPreviewRequest>();

/** Open an HTML document preview panel. Callable from anywhere. */
export const openHtmlPreview = bus.open;

/** Close the HTML preview panel. Callable from anywhere. */
export const closeHtmlPreview = bus.close;

// ─── Component ────────────────────────────────────────────────────────────

/**
 * Side panel that renders an HTML document in a sandboxed iframe.
 * Follows the same pattern as DocumentPreviewPanel — event-driven,
 * placed as a flex sibling of the main chat area.
 */
export function HtmlPreviewPanel() {
  const navigate = useNavigate();
  const { target, publishedAppFor } = useMicroAppUpdateTarget();
  const [preview, setPreview] = useState<HtmlPreviewRequest | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // The published app this HTML maps to (if any): an explicit appUuid on the
  // request, else an app published/updated for this artifact this session, else
  // the "Edit in chat" update target. Live data can only be fetched against a
  // published app + its captured bindings.
  const appUuid = preview?.appUuid ?? publishedAppFor(preview?.attachmentUuid) ?? target?.appUuid;
  const html = preview?.htmlContent ?? "";
  const hasBindings = useMemo(() => (html ? extractBindingKeys(html).length > 0 : false), [html]);
  // Wrap with the trusted hydration runtime only when we can actually hydrate
  // (we have a published app to fetch data from); otherwise render raw so the
  // publish-time fallback values still show.
  const srcDoc = useMemo(() => (html && appUuid ? injectHydrationRuntime(html) : html), [html, appUuid]);
  useDashboardHydration({ uuid: appUuid, html: appUuid ? html : "", iframeRef });

  // Subscribe to preview requests
  useEffect(() => {
    return bus.subscribe((req) => {
      if (!req) {
        handleClose();
        return;
      }
      // If clicking the same document again, close the panel
      if (preview?.attachmentUuid && preview.attachmentUuid === req.attachmentUuid) {
        handleClose();
        return;
      }
      setPreview(req);
      setShowSource(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview?.attachmentUuid]);

  const handleClose = useCallback(() => {
    setPreview(null);
    setExpanded(false);
    setShowSource(false);
  }, []);

  const handlePrint = useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe || !preview?.htmlContent) return;
    try {
      iframe.contentWindow?.print();
    } catch {
      // Fallback: open Blob URL in new window for printing
      const blob = new Blob([preview.htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (win) {
        win.addEventListener("afterprint", () => URL.revokeObjectURL(url));
        win.addEventListener("load", () => win.print());
      } else {
        URL.revokeObjectURL(url);
      }
    }
  }, [preview]);

  const handleDownload = useCallback(async () => {
    if (!preview) return;

    // If we have a URL and attachment UUID, download from the server
    if (preview.attachmentUuid) {
      try {
        const baseUrl = getApiBaseUrl();
        const headers = await getHeaders();
        headers.delete("Content-Type");
        const response = await fetch(`${baseUrl}/chat/attachments/${preview.attachmentUuid}/file`, { headers });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${preview.title}.html`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        return;
      } catch (err) {
        console.error("Failed to download from server, falling back to local content:", err);
      }
    }

    // Fallback: download from in-memory content
    if (preview.htmlContent) {
      const blob = new Blob([preview.htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${preview.title}.html`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }
  }, [preview]);

  if (!preview) return null;

  const panelWidth = expanded ? "w-[60%]" : "w-[500px]";

  return (
    <div
      className={`${panelWidth} flex flex-col border-l border-border-primary bg-surface-primary transition-all duration-300 min-w-0 overflow-hidden`}
    >
      {/* Header bar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary bg-surface-secondary shrink-0">
        <div className="flex items-center gap-2 min-w-0 mr-2">
          <span className="text-sm text-content-primary font-medium truncate">{preview.title}</span>
          {preview.version && preview.version > 1 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-brand-primary/10 text-brand-primary font-medium shrink-0">
              v{preview.version}
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          {appUuid && (
            <button
              onClick={() => navigate(`/apps/view/${appUuid}`)}
              className="p-1.5 hover:bg-interactive-active rounded text-brand-primary transition-colors"
              title="Open micro app"
            >
              <ExternalLink className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={() => setShowSource((s) => !s)}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title={showSource ? "Show Preview" : "View Source"}
          >
            {showSource ? <Eye className="w-4 h-4" /> : <Code className="w-4 h-4" />}
          </button>
          <button
            onClick={handlePrint}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title="Print / PDF"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button
            onClick={handleDownload}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title="Download HTML"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => setExpanded((e) => !e)}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title={expanded ? "Collapse" : "Expand"}
          >
            {expanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-interactive-active rounded text-content-secondary hover:text-content-primary transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {hasBindings && !appUuid && (
          <div className="flex items-start gap-2 px-3 py-2 text-xs text-content-tertiary bg-surface-secondary border-b border-border-primary">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            <span>
              Showing sample values. Live data loads once you publish this dashboard as a micro app (or open it from an
              existing app to edit).
            </span>
          </div>
        )}
        {showSource ? (
          <pre className="w-full flex-1 overflow-auto p-4 text-xs text-content-secondary font-mono bg-surface-primary whitespace-pre-wrap break-all">
            {preview.htmlContent}
          </pre>
        ) : (
          <iframe
            ref={iframeRef}
            srcDoc={srcDoc}
            sandbox="allow-scripts"
            title={`HTML Preview: ${preview.title}`}
            className="w-full flex-1 border-0"
            style={{ background: "white" }}
          />
        )}
      </div>
    </div>
  );
}
